import {
  buildTtqPipelineFlowSteps,
  hasTtqPipelineFlowSteps,
  type TtqPipelineFlowStepStatus,
} from './ttqPipelineFlow';
import type { TtqSessionParticleEntry } from '../../services/ttqService';

function statusClass(status: TtqPipelineFlowStepStatus): string {
  switch (status) {
    case 'done':
      return 'border-emerald-200 bg-emerald-50/80 text-emerald-900';
    case 'running':
      return 'border-blue-300 bg-blue-50 text-blue-900';
    case 'failed':
      return 'border-red-200 bg-red-50 text-red-900';
    case 'skipped':
      return 'border-gray-200 bg-gray-50 text-gray-400';
    default:
      return 'border-gray-200 bg-white text-gray-600';
  }
}

function statusBadgeClass(status: TtqPipelineFlowStepStatus): string {
  switch (status) {
    case 'done': return 'text-emerald-700';
    case 'running': return 'text-blue-700';
    case 'failed': return 'text-red-700';
    case 'skipped': return 'text-gray-400';
    default: return 'text-gray-500';
  }
}

const DETAIL_STEP_ORDERS = new Set([2, 3, 4, 5, 6]);

type TtqibFlowSequenceStripProps = {
  entry: TtqSessionParticleEntry | null | undefined;
  nodeId?: string;
  onInspectPipelineStep?: (nodeId: string, stepOrder: number) => void;
};

export function TtqibFlowSequenceStrip({ entry, nodeId, onInspectPipelineStep }: TtqibFlowSequenceStripProps) {
  if (!hasTtqPipelineFlowSteps(entry) || !entry) {
    return <p className="text-xs text-gray-400 italic">暂无品题流水线步骤</p>;
  }

  const steps = buildTtqPipelineFlowSteps(entry);

  return (
    <div
      className="nowheel nodrag grid grid-cols-6 gap-2"
      onWheel={(e) => e.stopPropagation()}
    >
      {steps.map((step) => {
        const clickable = DETAIL_STEP_ORDERS.has(step.order)
          && step.status === 'done'
          && Boolean(nodeId && onInspectPipelineStep);
        return (
          <div
            key={step.order}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? (e) => {
              e.stopPropagation();
              onInspectPipelineStep!(nodeId!, step.order);
            } : undefined}
            onKeyDown={clickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onInspectPipelineStep!(nodeId!, step.order);
              }
            } : undefined}
            className={
              `flex min-h-[72px] flex-col rounded-md border px-2 py-1.5 ${statusClass(step.status)}`
              + (clickable ? ' cursor-pointer hover:border-blue-400 hover:shadow-sm' : '')
            }
            title={clickable ? `点击查看${step.label}详情` : step.detail}
          >
            <span className={`text-[10px] font-bold leading-tight ${statusBadgeClass(step.status)}`}>
              步骤{step.order} · {step.label}
            </span>
            <span className="mt-1 line-clamp-4 text-[11px] leading-snug">
              {step.detail || (step.status === 'running' ? '进行中…' : step.status === 'skipped' ? '已跳过' : '待执行')}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export { hasTtqPipelineFlowSteps as hasFlowSequenceSteps };
